import { useState, useEffect, useRef, useCallback } from 'react';
import { HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { useAuth } from '../contexts/AuthContext';

export interface SignalRNotification {
  type: string;
  title: string;
  message: string;
  data?: Record<string, unknown>;
  timestamp: string;
  id: string;
  actionUrl?: string;
}

export interface SignalRJobCompleted {
  jobId: string;
  jobName: string;
  success: boolean;
  message: string;
  result?: unknown;
  timestamp: string;
}

const NOTIFICATIONS_INBOX_UPDATED = 'notifications-inbox-updated';

export function dispatchNotificationsInboxUpdated(notification?: SignalRNotification): void {
  window.dispatchEvent(
    new CustomEvent(NOTIFICATIONS_INBOX_UPDATED, { detail: notification ?? null })
  );
}

export const useSignalR = (
  onNotificationReceived?: (notification: SignalRNotification) => void,
  onJobCompleted?: (job: SignalRJobCompleted) => void
) => {
  const [connection, setConnection] = useState<HubConnection | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const { user } = useAuth();
  const connectionRef = useRef<HubConnection | null>(null);
  const onNotificationRef = useRef(onNotificationReceived);
  const onJobCompletedRef = useRef(onJobCompleted);

  onNotificationRef.current = onNotificationReceived;
  onJobCompletedRef.current = onJobCompleted;

  const stopConnection = useCallback(async () => {
    if (connectionRef.current)
    {
      try
      {
        await connectionRef.current.stop();
      }
      catch
      {
        // ignore stop errors during teardown
      }
      connectionRef.current = null;
      setConnection(null);
      setIsConnected(false);
    }
  }, []);

  const createConnection = useCallback(async () => {
    if (!user?.id)
    {
      return;
    }

    await stopConnection();

    try
    {
      const hubBaseUrl = process.env.REACT_APP_HUB_URL || 'https://localhost:7180';
      const hubUrl = `${hubBaseUrl}/hubs/notifications`;
      const accessToken = sessionStorage.getItem('@KnowInDoc:accessToken') ?? '';

      const newConnection = new HubConnectionBuilder()
        .withUrl(hubUrl, {
          withCredentials: true,
          accessTokenFactory: () => accessToken,
        })
        .withAutomaticReconnect([5000, 15000, 30000, 60000])
        .configureLogging(
          process.env.NODE_ENV === 'development' ? LogLevel.Warning : LogLevel.Error
        )
        .build();

      newConnection.on('NotificationReceived', (notification: SignalRNotification) => {
        dispatchNotificationsInboxUpdated(notification);
        onNotificationRef.current?.(notification);
      });

      newConnection.on('SupportNotificationReceived', (notification: SignalRNotification) => {
        window.dispatchEvent(
          new CustomEvent('support-notification-received', { detail: notification })
        );
      });

      newConnection.on('JobCompleted', (job: SignalRJobCompleted) => {
        onJobCompletedRef.current?.(job);
      });

      newConnection.onreconnected(() => {
        setIsConnected(true);
        setConnectionError(null);
      });

      newConnection.onclose(() => {
        setIsConnected(false);
      });

      await newConnection.start();

      connectionRef.current = newConnection;
      setConnection(newConnection);
      setIsConnected(true);
      setConnectionError(null);
    }
    catch (error: unknown)
    {
      const message = error instanceof Error ? error.message : 'Falha ao conectar SignalR';
      setConnectionError(message);
      setIsConnected(false);
      if (process.env.NODE_ENV === 'development')
      {
        console.warn('[SignalR] Não foi possível conectar ao hub de notificações:', message);
      }
    }
  }, [user?.id, stopConnection]);

  useEffect(() => {
    if (user?.id)
    {
      void createConnection();
    }
    else
    {
      void stopConnection();
    }

    return () => {
      void stopConnection();
    };
  }, [user?.id, createConnection, stopConnection]);

  return {
    connection,
    isConnected,
    connectionError,
  };
};

export { NOTIFICATIONS_INBOX_UPDATED };
